/**
 * Groups "skills", "perks" and "spec" of the progress ops: the skill/perk editor of the 'skills' screen, the perk
 * reset of the settings screen and the choice of a specialization.
 *
 * Original flow: the editor works on FWP.temp_player (a clone of the player, refresh_temp_params FWP.ts:4723):
 * 'add_attack' / 'add_protect' / 'add_health' / 'add_team_worm' (new_interf.ts:5804-5827), a new perk via 'add_perk<n>'
 * -> 'choose_perk' screen -> 'add_p' (6022-6032), a perk level via 'upg_perk<n>' (click_proceed 6242-6251), a perk
 * slot via 'buy_perk<n>' -> 'buy_perk' screen -> 'buy_gold' / 'buy_diam' (5828-5869). Nothing is checked by the
 * handlers; the buttons are shown only when the step is affordable (render 1598-1601, 1640-1660, 1776-1778,
 * 1782-1816). 'save' (5776-5794, visible only after a change) replaces the player by temp_player
 * (save_temp_params FWP.ts:4741) and advances tasks 13 -> 14 / 22 -> 23 (a new perk) and 19 -> 20 (health or worms).
 */
import { argsObject, intArg, knownKey, optionalIntArg, stringArg } from '../args';
import { clone, int, keys } from '../as3';
import { RuleError } from '../errors';
import { defineOp } from '../registry';
import type { PlayerState } from '../state';
import { attackCost, syncLevel } from './progress-common';

/** Perk slot prices of the 'buy_perk' screen by the current slot count (new_interf.ts:5830-5857, render 1782-1814). */
export const PERK_SLOT_PRICES: Readonly<Record<number, { gold: number; diam: number }>> = {
  2: { gold: 500, diam: 1 },
  3: { gold: 1000, diam: 1 },
  4: { gold: 5000, diam: 5 },
  5: { gold: 10000, diam: 10 },
  6: { gold: 20000, diam: 20 },
  7: { gold: 40000, diam: 40 },
  8: { gold: 60000, diam: 60 },
};

/** Skill points per health point / extra worm (new_interf.ts:5816-5827). */
export const HEALTH_COST = 30;
export const WORM_COST = 150;
/** add_team_worm is shown only while worms < 3 (render 1601). */
export const MAX_EXTRA_WORMS = 3;
/** Diamonds a perk reset costs (FWP.reset_perks). */
export const RESET_PERKS_DIAM = 10;

export interface SkillsCommitArgs {
  /** Target values (default: unchanged); they can only grow. */
  attack?: number;
  protect?: number;
  health?: number;
  worms?: number;
  /**
   * Target levels of perks that are added or upgraded (in the order the player added them: new perks are appended to
   * player.perks in this order). Perks not listed keep their level.
   */
  perks?: Record<string, number>;
  /** Perk slot purchases in order, each paid in gold or diamonds. */
  slots?: ('gold' | 'diam')[];
}

export interface SkillsCommitResult {
  skill_pts: number;
  perk_pts: number;
  gold: number;
  diam: number;
  newPerks: number;
}

const MAX_TARGET = 1_000_000;

/** FWP.get_perks_amount (FWP.ts:4755): perks with level > 0. */
function perksAmount(perks: Record<string, number>): number {
  let n = 0;
  for (const k of keys(perks)) if (perks[k as string] > 0) n++;
  return n;
}

function isSpecPerk(defs: { SPEC_DEF: any[] }, spec: number, id: string): boolean {
  if (spec == 0) return false;
  for (let i = 0; i <= 1; i++) if (defs.SPEC_DEF[spec]?.[i] == id) return true;
  return false;
}

export const skillsOps = {
  /**
   * skills.commit { attack?, protect?, health?, worms?, perks?, slots? }: the target values of one editing session of
   * the 'skills' screen, committed like 'save'. The server recomputes every cost from the current state:
   * attack/protect cost get_attack_cost(v) per point (1..5 by 100/150/200/250), health 30, a worm 150 (at most 3);
   * a new perk costs upg[0] and needs a free slot (perks < perk_slots) and must not be a perk of the specialization;
   * level l -> l + 1 costs upg[l] while l < max_lim; a slot costs the 2..8 price table in gold or diamonds. Every
   * spend only lowers its balance, so "affordable at every click" is the same as "the final balance is >= 0"; the
   * rule checks each step in a fixed order (slots, attack, protect, health, worms, perks).
   */
  'skills.commit': defineOp<SkillsCommitArgs, SkillsCommitResult>({
    parseArgs(raw) {
      const o = argsObject(raw, ['attack', 'protect', 'health', 'worms', 'perks', 'slots']);
      const out: SkillsCommitArgs = {};
      for (const f of ['attack', 'protect', 'health', 'worms'] as const) {
        const v = optionalIntArg(o, f, { min: 0, max: MAX_TARGET });
        if (v !== undefined) out[f] = v;
      }
      if (o.perks !== undefined) {
        const raw = o.perks;
        if (raw === null || typeof raw !== 'object' || Array.isArray(raw) || Object.getPrototypeOf(raw) !== Object.prototype) {
          throw new RuleError('bad_args', { field: 'perks', problem: 'must be an object of perk id -> level' });
        }
        const perks = raw as Record<string, unknown>;
        const t: Record<string, number> = {};
        for (const k of Object.keys(perks)) {
          if (!/^[A-Za-z_][A-Za-z0-9_]{0,47}$/.test(k)) throw new RuleError('bad_args', { field: `perks.${k}`, problem: 'bad perk id' });
          t[k] = intArg(perks, k, { min: 1, max: 100 });
        }
        out.perks = t;
      }
      if (o.slots !== undefined) {
        if (!Array.isArray(o.slots) || o.slots.length > 7) throw new RuleError('bad_args', { field: 'slots', problem: 'must be an array of at most 7 purchases' });
        out.slots = o.slots.map((s, i) => stringArg<'gold' | 'diam'>({ [`slots.${i}`]: s }, `slots.${i}`, { oneOf: ['gold', 'diam'] }));
      }
      return out;
    },
    run(state, args, ctx) {
      const p = state.player;
      const P = ctx.defs.PERKS_DEF;
      let skill = p.skill_pts;
      let perkPts = p.perk_pts;
      let gold = p.gold;
      let diam = p.diam;
      let slots = p.perk_slots;
      let changed = false;

      for (const cur of args.slots ?? []) {
        const price = PERK_SLOT_PRICES[slots];
        if (!price) throw new RuleError('limit_reached', { perk_slots: slots });
        if (cur === 'gold') {
          if (!(gold >= price.gold)) throw new RuleError('not_enough_gold', { have: gold, need: price.gold });
          gold = int(gold - price.gold);
        } else {
          if (!(diam >= price.diam)) throw new RuleError('not_enough_diam', { have: diam, need: price.diam });
          diam = int(diam - price.diam);
        }
        ++slots;
        changed = true;
      }

      const target = {
        attack: args.attack ?? p.attack,
        protect: args.protect ?? p.protect,
        health: args.health ?? p.health,
        worms: args.worms ?? p.worms,
      };
      for (const f of ['attack', 'protect', 'health', 'worms'] as const) {
        if (target[f] < p[f]) throw new RuleError('cannot_decrease', { field: f, have: p[f], target: target[f] });
        if (target[f] != p[f]) changed = true;
      }
      if (target.worms > MAX_EXTRA_WORMS) throw new RuleError('limit_reached', { field: 'worms', max: MAX_EXTRA_WORMS });
      for (const f of ['attack', 'protect'] as const) {
        for (let v = p[f]; v < target[f]; v++) {
          const cost = attackCost(v);
          if (!(skill >= cost)) throw new RuleError('not_enough_skill_pts', { field: f, have: skill, need: cost });
          skill = int(skill - cost);
        }
      }
      for (let v = p.health; v < target.health; v++) {
        if (!(skill >= HEALTH_COST)) throw new RuleError('not_enough_skill_pts', { field: 'health', have: skill, need: HEALTH_COST });
        skill = int(skill - HEALTH_COST);
      }
      for (let v = p.worms; v < target.worms; v++) {
        if (!(skill >= WORM_COST)) throw new RuleError('not_enough_skill_pts', { field: 'worms', have: skill, need: WORM_COST });
        skill = int(skill - WORM_COST);
      }

      const perks: Record<string, number> = clone(p.perks);
      for (const id of Object.keys(args.perks ?? {})) {
        const want = args.perks![id];
        knownKey(P, id, 'perks');
        const def = P[id];
        let lvl: number;
        if (!Object.prototype.hasOwnProperty.call(perks, id)) {
          // 'add_p' is shown for a perk the player does not have, that is not given by the specialization, with enough
          // points (render 1776-1778), and the add_perk<n> buttons only while a slot is free (render 1640-1660).
          if (isSpecPerk(ctx.defs, p.spec, id)) throw new RuleError('not_available', { perk: id, problem: 'given by the specialization' });
          if (!(keys(perks).length < slots)) throw new RuleError('no_free_slot', { perks: keys(perks).length, perk_slots: slots });
          if (!(perkPts >= def.upg[0])) throw new RuleError('not_enough_perk_pts', { perk: id, have: perkPts, need: def.upg[0] });
          perks[id] = 1;
          perkPts = int(perkPts - def.upg[0]);
          lvl = 1;
          changed = true;
        } else {
          lvl = perks[id];
          if (want < lvl) throw new RuleError('cannot_decrease', { perk: id, have: lvl, target: want });
          // upg_perk<n> is shown only for a perk of level >= 1 (render 1646).
          if (want > lvl && !(lvl >= 1)) throw new RuleError('not_available', { perk: id, level: lvl });
        }
        while (lvl < want) {
          if (!(lvl < def.max_lim)) throw new RuleError('limit_reached', { perk: id, max_lim: def.max_lim });
          const cost = def.upg[lvl];
          if (!(perkPts >= cost)) throw new RuleError('not_enough_perk_pts', { perk: id, have: perkPts, need: cost });
          perkPts = int(perkPts - cost);
          ++lvl;
          changed = true;
        }
        perks[id] = lvl;
      }
      // 'save' is visible only after a change (render 1661-1662: svtemp.visible = changes_made).
      if (!changed) throw new RuleError('no_changes');

      const newPerks = perksAmount(perks) - perksAmount(p.perks);
      const healthOrWorms = p.health != target.health || p.worms != target.worms;
      const spent = { skill_pts: p.skill_pts - skill, perk_pts: p.perk_pts - perkPts, gold: p.gold - gold, diam: p.diam - diam };
      p.attack = int(target.attack);
      p.protect = int(target.protect);
      p.health = int(target.health);
      p.worms = int(target.worms);
      p.skill_pts = int(skill);
      p.perk_pts = int(perkPts);
      p.gold = int(gold);
      p.diam = int(diam);
      p.perk_slots = slots;
      p.perks = perks;
      if (newPerks > 0) {
        if (p.cur_task == 13) p.cur_task = 14;
        if (p.cur_task == 22) p.cur_task = 23;
      }
      if (healthOrWorms) {
        if (p.cur_task == 19) p.cur_task = 20;
      }
      return { state, result: { ...spent, newPerks } };
    },
  }),

  /**
   * perks.reset: 'reset_perks' -> 'question' -> 'proceed' (new_interf.ts:5664, 6033-6038) -> FWP.reset_perks
   * (FWP.ts:6973-7004). Precondition (render 3182: proceed visible): diam >= 10 and (last_reset == 0 or
   * level >= last_reset + 5), the level derived from exp first (stale-level fix).
   */
  'perks.reset': defineOp<Record<string, never>, { perk_pts: number; skill_pts: number }>({
    parseArgs(raw) {
      argsObject(raw, []);
      return {};
    },
    run(state, _args, ctx) {
      const p = state.player;
      const P = ctx.defs.PERKS_DEF;
      syncLevel(p, ctx.defs);
      if (!(p.diam >= RESET_PERKS_DIAM)) throw new RuleError('not_enough_diam', { have: p.diam, need: RESET_PERKS_DIAM });
      if (!(p.last_reset == 0 || p.level >= p.last_reset + 5)) throw new RuleError('level_too_low', { level: p.level, need: p.last_reset + 5 });
      // The original loops `while (x != 0)`: a state it would hang on is refused instead.
      for (const k of keys(p.perks)) {
        const l = p.perks[k as string];
        if (!Object.prototype.hasOwnProperty.call(P, k) || !Number.isInteger(l) || l < 0 || l > P[k as string].upg.length) throw new RuleError('bad_state', { perk: k, level: l });
      }
      if (p.attack < 0 || p.protect < 0) throw new RuleError('bad_state', { attack: p.attack, protect: p.protect });

      const before = { perk_pts: p.perk_pts, skill_pts: p.skill_pts };
      p.diam = int(p.diam - RESET_PERKS_DIAM);
      let pts = 0;
      const levels: Record<string, number> = clone(p.perks);
      for (const k of keys(p.perks)) {
        const key = k as string;
        while (levels[key] != 0) {
          pts += P[key].upg[levels[key] - 1];
          --levels[key];
        }
      }
      p.perk_pts = int(p.perk_pts + pts);
      p.perks = {};
      // `while (attack != 0) { skill_pts += get_attack_cost(attack - 1); --attack }` in closed form (the same int32
      // result: wrapping per step or once gives the same value mod 2^32), so a huge attack cannot stall the server.
      p.skill_pts = int(p.skill_pts + attackRefund(p.attack));
      p.skill_pts = int(p.skill_pts + attackRefund(p.protect));
      p.skill_pts = int(p.skill_pts + p.health * HEALTH_COST);
      p.skill_pts = int(p.skill_pts + p.worms * WORM_COST);
      p.attack = 0;
      p.protect = 0;
      p.health = 0;
      p.worms = 0;
      p.spec = 0;
      p.last_reset = p.level;
      return { state, result: { perk_pts: p.perk_pts - before.perk_pts, skill_pts: p.skill_pts - before.skill_pts } };
    },
  }),

  /**
   * spec.choose { spec }: 'choose_spec' (visible while spec == 0, render 1603) -> 'click_spec_<n>' (n = 1..4,
   * new_interf.ts:5701-5710) -> 'finish_choose_spec' (5711-5716) -> FWP.improve_spec (FWP.ts:7006-7038): the two
   * perks of the specialization are removed and their first three levels refunded.
   */
  'spec.choose': defineOp<{ spec: number }, { spec: number; perk_pts: number }>({
    parseArgs(raw) {
      const o = argsObject(raw, ['spec']);
      return { spec: intArg(o, 'spec', { min: 1, max: 4 }) };
    },
    run(state, { spec }, ctx) {
      const p = state.player;
      const P = ctx.defs.PERKS_DEF;
      if (p.spec != 0) throw new RuleError('not_available', { spec: p.spec });
      const before = p.perk_pts;
      p.spec = spec;
      const s = p.spec;
      const kept: Record<string, number> = {};
      for (const k of keys(p.perks)) {
        const key = k as string;
        let isSpec = false;
        for (let i = 0; i <= 1; i++) {
          if (key == ctx.defs.SPEC_DEF[s][i]) {
            if (p.perks[key] > 0) p.perk_pts = int(p.perk_pts + P[key].upg[0]);
            if (p.perks[key] > 1) p.perk_pts = int(p.perk_pts + P[key].upg[1]);
            if (p.perks[key] > 2) p.perk_pts = int(p.perk_pts + P[key].upg[2]);
            isSpec = true;
          }
        }
        if (!isSpec) kept[key] = p.perks[key];
      }
      p.perks = clone(kept);
      return { state, result: { spec, perk_pts: p.perk_pts - before } };
    },
  }),
};

/** Sum of get_attack_cost(v) for v = 0 .. a - 1: the skill points spent on `a` points of attack or protect. */
export function attackRefund(a: number): number {
  const band = (lo: number, hi: number) => Math.max(0, Math.min(a, hi) - lo);
  return band(0, 100) * 1 + band(100, 150) * 2 + band(150, 200) * 3 + band(200, 250) * 4 + Math.max(0, a - 250) * 5;
}

/** Exported for tests: the perk slot price of a player (undefined when no slot can be bought). */
export function perkSlotPrice(p: Pick<PlayerState, 'perk_slots'>): { gold: number; diam: number } | undefined {
  return PERK_SLOT_PRICES[p.perk_slots];
}
